// client/src/App.js
// Rotkomponent och router. Lindar appen i HealthDataProvider + AuthProvider
// och har en PasswordProtection-gate runt allt innehåll.
import './i18n';  
import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import { HealthDataProvider } from './context/HealthDataContext';
import { AuthProvider } from './context/AuthContext';
import AppHeader from './components/shared/navigation/AppHeader';

// 🆕 NEW: Home page import
import Home from './pages/Home/Home';

// 🔄 EXISTING: All existing pages
import Dashboard from './pages/Dashboard/Dashboard';
import DataAnalytics from './pages/DataAnalytics/DataAnalytics';
import UserReports from './pages/UserReports/UserReports';
import Recovery from './pages/Recovery/Recovery';
import MapView from './pages/MapView/MapView';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import './App.css';

/**
 * PasswordProtection - Skyddar affärshemlighet från obehöriga
 */
const PasswordProtection = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(
    localStorage.getItem('health_app_authenticated') === 'true'
  );
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // ✅ Ditt hemliga lösenord
    const correctPassword = 'howareyou2025';
    
    if (password === correctPassword) {
      setAuthenticated(true);
      localStorage.setItem('health_app_authenticated', 'true');
      setError('');
    } else {
      setError('Fel lösenord - försök igen');
      setPassword('');
    }
  };

  if (authenticated) {
    return children;
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f7fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '50px 40px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        width: '400px',
        textAlign: 'center',
        border: '1px solid #e1e8ed'
      }}>
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ 
            margin: '0 0 10px 0', 
            color: '#2c3e50',
            fontSize: '28px',
            fontWeight: '600'
          }}>
            EpiMeter
          </h1>
          <p style={{ 
            color: '#7f8c8d', 
            margin: '0 0 30px 0',
            fontSize: '16px'
          }}>
            Säker åtkomst krävs för att fortsätta
          </p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ange lösenord"
              style={{
                width: '100%',
                padding: '15px',
                border: error ? '2px solid #e74c3c' : '2px solid #bdc3c7',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.3s',
                boxSizing: 'border-box'
              }}
              autoFocus
              onFocus={(e) => {
                e.target.style.borderColor = '#3498db';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = error ? '#e74c3c' : '#bdc3c7';
              }}
            />
          </div>
          
          {error && (
            <div style={{ 
              color: '#e74c3c', 
              marginBottom: '20px', 
              fontSize: '14px',
              padding: '10px',
              backgroundColor: '#fdf2f2',
              borderRadius: '6px',
              border: '1px solid #fecaca'
            }}>
              ❌ {error}
            </div>
          )}
          
          <button
            type="submit"
            style={{
              width: '100%',
              padding: '15px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.3s',
              marginBottom: '20px'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#2980b9';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#3498db';
            }}
          >
            🔐 Logga in
          </button>
        </form>
        
        <div style={{ 
          borderTop: '1px solid #ecf0f1',
          paddingTop: '20px',
          fontSize: '12px', 
          color: '#95a5a6'
        }}>
          <p style={{ margin: '0 0 5px 0' }}>
            🔒 Lösenordet sparas säkert i din webbläsare
          </p>
          <p style={{ margin: '0' }}>
            💼 Skyddar affärshemligheter och konfidentiell data
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * ✅ NEW: AppContentWrapper - Conditional CSS class based on route
 */
const AppContentWrapper = ({ children }) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  return (
    <main className={`app-content ${isHomePage ? 'home-page-content' : ''}`}>
      {children}
    </main>
  );
};

/**
 * App - ✅ PUBLIC ACCESS: Alla huvudsidor utan PrivateRoute
 */
function App() {
  return (
    <PasswordProtection>
      <AuthProvider>
        <Router future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}>
          <div className="app">
            {/* 🧭 GLOBAL NAVIGATION - Visas på alla sidor */}
            <AppHeader />
            
            {/* Main content area med conditional CSS class */}
            <AppContentWrapper>
              <Routes>
                {/* 🆕 Home som startsida */}
                <Route path="/" element={<Home />} />
                
                {/* ✅ PUBLIC: Smittläge (tidigare Dashboard) */}
                <Route path="/smittlage" element={
                  <HealthDataProvider>
                    <Dashboard />
                  </HealthDataProvider>
                } />
                
                {/* ✅ PUBLIC: Analytics utan inloggning */}
                <Route path="/analytics" element={<DataAnalytics />} />
                
                {/* ✅ PUBLIC: MapView utan inloggning */}
                <Route path="/map" element={<MapView />} />
                
                {/* ✅ PUBLIC: UserReports */}
                <Route path="/reports" element={<UserReports />} />
                <Route path="/recovery/:code" element={<Recovery />} />
                
                {/* Auth routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                
                {/* Fallback route för 404 */}
                <Route path="*" element={
                  <div className="not-found">
                    <h1>
                      <span role="img" aria-label="Not Found">❌</span>
                      Sida inte hittad
                    </h1>
                    <p>Sidan du söker existerar inte.</p>
                    <Link to="/" className="back-link">
                      <span role="img" aria-label="Home">🏠</span>
                      Tillbaka till Hem
                    </Link>
                  </div>
                } />
              </Routes>
            </AppContentWrapper>
            
            {/* Footer */}
            <footer className="app-footer">
              <div className="footer-content">
                <p>&copy; 2025 EpiMeter. All rights reserved.</p>
                <div className="footer-links">
                  <span className="system-status">
                    <span role="img" aria-label="Status">✅</span>
                    System Operational
                  </span>
                </div>
              </div>
            </footer>
          </div>
        </Router>
      </AuthProvider>
    </PasswordProtection>
  );
}

export default App;
